import { createSlice } from "@reduxjs/toolkit";
import {
  fetchTournamentExpenses,
  fetchTournamentExpenseById,
  addTournamentExpense,
  updateTournamentExpenseThunk,
  deleteTournamentExpenseThunk,
} from "./tournamentExpenses.thunk";

const initialState = {
  expenses: [],
  meta: {
    total: 0,
    total_pages: 1,
    page: 1,
    limit: 10,
  },
  loading: false,
  error: null,
  selectedExpense: null,
};

const tournamentExpensesSlice = createSlice({
  name: "tournamentExpenses",
  initialState,

  reducers: {
    setSelectedExpense: (state, action) => {
      state.selectedExpense = action.payload;
    },

    clearSelectedExpense: (state) => {
      state.selectedExpense = null;
    },
  },

  extraReducers: (builder) => {
    builder

      /* ============================================================
         FETCH ALL
      ============================================================ */
      .addCase(fetchTournamentExpenses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchTournamentExpenses.fulfilled, (state, action) => {
        state.loading = false;

        const payload = action.payload;

        let expenses = [];

        if (Array.isArray(payload?.data)) {
          expenses = payload.data;
        } else if (Array.isArray(payload?.data?.data)) {
          expenses = payload.data.data;
        }

        state.expenses = expenses;

        state.meta = payload?.meta || {
          total: expenses.length,
          total_pages: 1,
          page: 1,
          limit: 10,
        };
      })

      .addCase(fetchTournamentExpenses.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Failed to fetch expenses";
      })

      /* ============================================================
         FETCH SINGLE
      ============================================================ */
      .addCase(fetchTournamentExpenseById.pending, (state) => {
        state.loading = true;
      })

      .addCase(fetchTournamentExpenseById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedExpense = action.payload;
      })

      .addCase(fetchTournamentExpenseById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ============================================================
         ADD
      ============================================================ */
      .addCase(addTournamentExpense.pending, (state) => {
        state.loading = true;
      })

      .addCase(addTournamentExpense.fulfilled, (state) => {
        state.loading = false;
        // Refetch handled in component
      })

      .addCase(addTournamentExpense.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ============================================================
         UPDATE
      ============================================================ */
      .addCase(updateTournamentExpenseThunk.pending, (state) => {
        state.loading = true;
      })

      .addCase(updateTournamentExpenseThunk.fulfilled, (state, action) => {
        state.loading = false;

        const updated = action.payload?.data;

        if (!updated) return;

        const id =
          action.payload.id ||
          updated.expense_id ||
          updated.id;

        const index = state.expenses.findIndex(
          (expense) =>
            expense.expense_id === id ||
            expense.id === id
        );

        if (index !== -1) {
          state.expenses[index] = {
            ...state.expenses[index],
            ...updated,
          };
        }

        if (
          state.selectedExpense &&
          (state.selectedExpense.expense_id === id ||
            state.selectedExpense.id === id)
        ) {
          state.selectedExpense = {
            ...state.selectedExpense,
            ...updated,
          };
        }
      })

      .addCase(updateTournamentExpenseThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ============================================================
         DELETE
      ============================================================ */
      .addCase(deleteTournamentExpenseThunk.pending, (state) => {
        state.loading = true;
      })

      .addCase(deleteTournamentExpenseThunk.fulfilled, (state, action) => {
        state.loading = false;

        const id = action.payload;

        state.expenses = state.expenses.filter(
          (expense) =>
            expense.expense_id !== id &&
            expense.id !== id
        );

        if (
          state.selectedExpense &&
          (state.selectedExpense.expense_id === id ||
            state.selectedExpense.id === id)
        ) {
          state.selectedExpense = null;
        }
      })

      .addCase(deleteTournamentExpenseThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSelectedExpense,
  clearSelectedExpense,
} = tournamentExpensesSlice.actions;

export default tournamentExpensesSlice.reducer;